import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { dateTime } from '@/lib/format';
import { getProspectRun, listProspectRuns } from '@/data/client';
import type { ProspectRun } from '@/data/types';

/* Search history with cost, and per search the sheet's "Run Details": every
   business checked and why it was or wasn't added, near misses first. */
export function ProspectRuns() {
  const { data: runs = [], isLoading } = useQuery({
    queryKey: ['prospectRuns'], queryFn: listProspectRuns,
    // Poll while something is still running or waiting on ad checks.
    refetchInterval: (q) => ((q.state.data ?? []).some((r) => r.status === 'queued' || r.status === 'running' || r.adsPending > 0) ? 5000 : false),
  });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card padding={false}>
      <table className="dash-table lead-table">
        <thead><tr><th>When</th><th>Search</th><th>Result</th><th className="crm-num">Cost</th><th>By</th></tr></thead>
        <tbody>
          {runs.map((r) => (
            <RunRow key={r.id} run={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} />
          ))}
        </tbody>
      </table>
      {!isLoading && !runs.length && <p className="lead-none">No searches yet — start one from Find.</p>}
    </Card>
  );
}

function RunRow({ run, open, onToggle }: { run: ProspectRun; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr onClick={onToggle}>
        <td className="crm-cell-muted pq-nowrap"><Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} /> {dateTime(run.createdAt)}</td>
        <td>
          {run.mode === 'term'
            ? <span className="pq-run-term"><strong>“{run.keyword}”</strong> <Badge variant="accent">Search term</Badge></span>
            : <strong>{run.category}</strong>}
          <span className="crm-cell-sub">{run.city}, {run.state}</span>
        </td>
        <td>
          {run.status === 'failed' ? <Badge variant="warning">Failed</Badge>
            : run.status !== 'done' ? <Badge variant="accent">Running…</Badge>
              : <span>{run.added} new prospect{run.added === 1 ? '' : 's'}</span>}
          {run.adsPending > 0 && <span className="crm-cell-sub">{run.adsPending} ad checks still running</span>}
          {run.stats && <span className="crm-cell-sub">{run.stats.businesses} businesses checked · {run.stats.passedSeo} matched the ranking gap</span>}
          {(run.error || run.notes) && <span className="crm-cell-sub">{run.error ?? run.notes}</span>}
        </td>
        <td className="crm-num">${run.cost.toFixed(2)}</td>
        <td className="crm-cell-muted">{run.runBy ?? '—'}</td>
      </tr>
      {open && <tr className="pq-details-row"><td colSpan={5}><RunDetails id={run.id} /></td></tr>}
    </>
  );
}

function RunDetails({ id }: { id: string }) {
  const { data } = useQuery({ queryKey: ['prospectRun', id], queryFn: () => getProspectRun(id) });
  if (!data) return <p className="crm-cell-muted">Loading…</p>;
  if (!data.details.length) return <p className="crm-cell-muted">No details yet.</p>;
  // A search-term run shows which Setup category each business was mapped to (or Google's label when none matched).
  const term = data.settings?.mode === 'term';
  return (
    <table className="pq-details">
      <thead><tr><th>Outcome</th><th>Business</th>{term && <th>Category</th>}<th className="crm-num">Reviews</th><th className="crm-num">Best pos.</th><th className="crm-num">In range</th><th>Ads</th></tr></thead>
      <tbody>
        {data.details.map((d, i) => (
          <tr key={i} className={d.outcome === 'PROSPECT' ? 'is-added' : d.outcome.startsWith('Previously') ? 'is-previous' : ''}>
            <td>{d.outcome === 'PROSPECT' ? 'Added' : d.outcome}{d.crmNote ? <span className="crm-cell-sub">{d.crmNote}</span> : null}</td>
            <td>
              {d.name || '—'}<span className="crm-cell-sub">{d.domain}</span>
              {d.provider ? <span className="crm-cell-sub">Site by {d.provider}</span> : d.providerStatus === 'unreadable' ? <span className="crm-cell-sub pq-flag">Site could not be read</span> : null}
            </td>
            {term && <td>{d.category ?? (d.googleCategory ? <span className="pq-flag">{d.googleCategory}</span> : '—')}</td>}
            <td className="crm-num">{d.reviews ?? '—'}</td>
            <td className="crm-num">{d.bestPosition ?? '—'}</td>
            <td className="crm-num">{d.inRange || '—'}</td>
            <td>{d.adsSeen ? 'Live ads' : d.paidKeywords ? `${d.paidKeywords} paid kw` : d.transparency ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
